import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Phase, Workout, WorkoutType } from 'fitforce-document-sync';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Day } from '../document-calendar/document-calendar.component';
import {
  DocumentAddWorkoutDialogComponent,
  DocumentAddWorkoutDialogResult,
} from './document-add-workout-dialog/document-add-workout-dialog.component';

@Component({
  selector: 'document-workout-selector',
  templateUrl: './document-workout-selector.component.html',
  styleUrls: ['./document-workout-selector.component.scss'],
})
export class DocumentWorkoutSelectorComponent implements OnInit, OnDestroy, OnChanges {
  workoutControl: FormControl;
  workoutControlSubscription: Subscription = new Subscription();
  dialogOpenedSubscription: Subscription = new Subscription();
  dialogClosedSubscription: Subscription = new Subscription();

  private _workoutList: { title: string; id: string; complete: boolean }[];

  @Input() readonly day: Day;
  @Input() readonly canEdit: boolean;

  @Output() workoutSelected: EventEmitter<Day> = new EventEmitter<Day>();
  @Output() standardWorkoutAdded: EventEmitter<{ day: Day; workoutType: WorkoutType }> = new EventEmitter<{
    day: Day;
    workoutType: WorkoutType;
  }>();
  @Output() customWorkoutAdded: EventEmitter<{ day: Day }> = new EventEmitter<{ day: Day }>();
  @Output() restrictedWorkoutAdded: EventEmitter<{ day: Day }> = new EventEmitter<{ day: Day }>();
  @Output() workoutAddedFromLibrary: EventEmitter<{ day: Day }> = new EventEmitter<{ day: Day }>();
  get workoutList(): { title: string; id: string; complete: boolean }[] {
    if (!this._workoutList && this.workouts) {
      this._workoutList = this.workouts.map(workout => {
        return {
          title: workout.name,
          id: workout.id,
          complete: workout.complete,
        };
      });
    }

    return this._workoutList;
  }

  get workout(): Workout {
    return this.day.workout;
  }

  get workouts(): Workout[] {
    return this.day ? this.day.workouts : undefined;
  }

  get phase(): Phase {
    return this.day.phase;
  }

  get date(): moment.Moment {
    return this.day.date;
  }

  constructor(private dialog: MatDialog) {}

  ngOnChanges() {
    this._workoutList = undefined;

    if (!this.workoutControl) {
      this.workoutControl = new FormControl();
    }

    this.workoutControl.setValue(this.day ? this.day.workout.id : undefined, { emitEvent: false });
  }

  ngOnInit() {
    if (!this.workoutControl) {
      this.workoutControl = new FormControl();
    }

    this.workoutControlSubscription = this.workoutControl.valueChanges.pipe(debounceTime(500)).subscribe(value => {
      if (value === 'null') {
        const dialogRef = this.dialog.open(DocumentAddWorkoutDialogComponent, {
          width: '600px',
        });

        this.dialogOpenedSubscription = dialogRef
          .afterOpened()
          .pipe(debounceTime(500))
          .subscribe(() => this.resetWorkoutControl());

        this.dialogClosedSubscription = dialogRef.beforeClosed().subscribe(result => {
          if (result) {
            if (result.type === DocumentAddWorkoutDialogResult.AddStandardWorkout) {
              this.standardWorkoutAdded.emit({ day: this.day, workoutType: result.workoutType });
            } else if (result.type === DocumentAddWorkoutDialogResult.AddCustomWorkout) {
              this.customWorkoutAdded.emit({ day: this.day });
            } else if (result.type === DocumentAddWorkoutDialogResult.AddRestrictedWorkout) {
              this.restrictedWorkoutAdded.emit({ day: this.day });
            } else if (result.type === DocumentAddWorkoutDialogResult.AddFromLibrary) {
              this.workoutAddedFromLibrary.emit({ day: this.day });
            }
          }
        });
      } else {
        const workout = this.workouts.find(wkt => wkt.id === value);
        if (workout) {
          this.day.workout = workout;
          this.workoutSelected.emit(this.day);
        }
      }
    });
  }

  ngOnDestroy() {
    this.workoutControlSubscription.unsubscribe();
    this.dialogClosedSubscription.unsubscribe();
    this.dialogOpenedSubscription.unsubscribe();
  }

  private resetWorkoutControl() {
    this.workoutControl.setValue(this.day ? (this.day.workout ? this.day.workout.id : undefined) : undefined, { emitEvent: false });
  }
}
